import { useEffect } from 'react';
import { useAuth } from '@src/presentation/contexts/AuthContext';
import { usePermission } from '@src/presentation/hooks/usePermission';
import { getAnalyticsService } from '@src/container';

/**
 * Analytics User Tracker Component
 * Tracks user properties when user logs in/out
 * Ensures consistent user identification across sessions
 */
export function AnalyticsUserTracker() {
    const { user, isAuthenticated } = useAuth();
    const { isSuperAdmin } = usePermission();

    useEffect(() => {
        const analyticsService = getAnalyticsService();

        if (!analyticsService.isInitialized()) {
            return;
        }

        if (isAuthenticated && user) {
            // Set user properties when user is authenticated
            // This ensures all subsequent events are attributed to the same user
            analyticsService.setUserProperties({
                userId: user.userId,
                userRole: user.roles?.map(role => role.name).join(', ') || 'user',
                universityId: user.universityId || undefined,
                properties: {
                    email_verified: user.isVerified,
                    has_roles: (user.roles?.length || 0) > 0,
                    is_super_admin: isSuperAdmin(),
                },
            }).catch((error) => {
                console.error('Failed to set user properties:', error);
            });

            // Log for debugging in development
            if (import.meta.env.DEV) {
                console.log('[Analytics] User identified:', {
                    userId: user.userId,
                    roles: user.roles?.map(role => role.name).join(', '),
                });
            }
        } else {
            // Clear user data when user logs out
            // This prevents attribution to the wrong user in shared environments
            analyticsService.clearUserData().catch((error) => {
                console.error('Failed to clear user data:', error);
            });

            if (import.meta.env.DEV) {
                console.log('[Analytics] User data cleared');
            }
        }
    }, [user, isAuthenticated, isSuperAdmin]);

    return null;
}
